import { useState } from 'react';

import { Button } from '@/components/ui/catalyst/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

// Define a more specific type for individual section data based on backend structure
interface SectionDetailFromBackend {
  confidence: number;
  text?: string;
  image?: string;
  image_url?: string;
  block_ids?: string[];
  bounding_boxes?: Record<string, any>;
}

const SectionImage = ({
  sectionName,
  imagePath,
}: {
  sectionName: string;
  imagePath: string;
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const handleImageLoad = () => {
    setIsLoading(false);
  };

  const handleImageError = () => {
    setError('Failed to load image');
    setIsLoading(false);
  };

  if (error) {
    return (
      <div className="w-full h-48 bg-muted rounded-lg flex items-center justify-center">
        <span className="text-sm text-red-500">{error}</span>
      </div>
    );
  }

  return (
    <div className="w-full relative">
      {isLoading && (
        <div className="absolute inset-0 bg-muted animate-pulse rounded-lg flex items-center justify-center">
          <span className="text-sm text-muted-foreground">
            Loading image...
          </span>
        </div>
      )}
      <img
        src={imagePath}
        alt={`${sectionName} section`}
        className="w-full h-auto rounded-lg border shadow-sm"
        onLoad={handleImageLoad}
        onError={handleImageError}
      />
    </div>
  );
};

interface SectionAnalysisProps {
  sections: Record<string, any>;
  onClose: () => void;
  onSubmit: () => Promise<void>;
  isSubmitting?: boolean;
}

export const SectionAnalysis = ({
  sections,
  onClose,
  onSubmit,
  isSubmitting = false,
}: SectionAnalysisProps) => {
  if (
    !sections ||
    typeof sections !== 'object' ||
    (Object.prototype.hasOwnProperty.call(sections, 'error') && sections.error)
  ) {
    return null;
  }

  // After the guard, 'sections' is confirmed to be Record<string, any>
  const orderedSectionEntries = Object.entries(sections as Record<string, any>)
    .map(([sectionName, sectionData]) => ({ sectionName, sectionData }))
    .sort((a, b): number => {
      const extractMinPage = (data: any): number => {
        if (!Array.isArray(data.block_ids)) return 1e9;
        const pages = data.block_ids
          .map((id: string) => {
            const match = id.match(/\/page\/(\d+)/);
            return match ? parseInt(match[1], 10) : undefined;
          })
          .filter(
            (n: number | undefined): n is number => typeof n === 'number',
          );
        return pages.length ? Math.min(...pages) : 1e9;
      };

      const pageA = extractMinPage(a.sectionData);
      const pageB = extractMinPage(b.sectionData);
      if (pageA !== pageB) return pageA - pageB;

      // fallback to provided order if page numbers are equal or missing
      const orderA =
        typeof a.sectionData.order === 'number' ? a.sectionData.order : 1e9;
      const orderB =
        typeof b.sectionData.order === 'number' ? b.sectionData.order : 1e9;
      return orderA - orderB;
    });

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Document Sections Analysis</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-6 py-4">
          {orderedSectionEntries.length > 0 ? (
            orderedSectionEntries.map(({ sectionName, sectionData }) => {
              const details = sectionData as SectionDetailFromBackend;
              const imagePath = details.image_url || details.image;

              return (
                <div key={sectionName} className="flex flex-col gap-3">
                  <div className="flex items-center gap-2">
                    <h4 className="text-base font-medium capitalize">
                      {sectionName.replace(/_/g, ' ')}
                    </h4>
                    {typeof details.confidence === 'number' && (
                      <span className="text-sm text-muted-foreground">
                        (Confidence: {Math.round(details.confidence * 100)}%)
                      </span>
                    )}
                  </div>
                  {imagePath && (
                    <SectionImage
                      sectionName={sectionName}
                      imagePath={imagePath}
                    />
                  )}
                </div>
              );
            })
          ) : (
            <p className="text-muted-foreground text-center py-4">
              No section data to display according to the defined order or data
              is missing.
            </p>
          )}
        </div>

        <DialogFooter>
          <Button outline onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={onSubmit} disabled={isSubmitting}>
            {isSubmitting ? 'Submitting...' : 'Submit Analysis'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
